import { ref } from 'vue';

import { PatientDetails } from '../../types';

/**
 * Who the request is about, and how far they have been verified. Shared by
 * every flow that identifies a patient.
 *
 * The three flags are distinct and not interchangeable:
 * - `isValidPatient` — the submitted demographics matched a patient record
 * - `isVerified`     — a verification code (2FA) was accepted
 * - `isNhsVerified`  — the patient arrived via NHS login
 *
 * Proxy state deliberately lives outside this composable: only medical and
 * admin requests can be made by a proxy.
 */
export function usePatientIdentity() {
  const patientDetails = ref<PatientDetails | null>(null);
  const isValidPatient = ref<boolean>(false);
  const isVerified = ref<boolean>(false);
  const isNhsVerified = ref<boolean>(false);

  function setPatientDetails(details: PatientDetails | null) {
    patientDetails.value = details;
  }

  function setIsValidPatient(value: boolean) {
    isValidPatient.value = value;
  }

  function setIsVerified(value: boolean) {
    isVerified.value = value;
  }

  function setIsNhsVerified(value: boolean) {
    isNhsVerified.value = value;
  }

  function resetIdentity() {
    patientDetails.value = null;
    isValidPatient.value = false;
    isVerified.value = false;
    isNhsVerified.value = false;
  }

  return {
    patientDetails,
    isValidPatient,
    isVerified,
    isNhsVerified,
    setPatientDetails,
    setIsValidPatient,
    setIsVerified,
    setIsNhsVerified,
    resetIdentity,
  };
}
